import { Info } from "lucide-react";
import { generateShadeHex } from "./helpers";

function OrderGuide() {
  return (
    <div
      className="pastel-green border border-[var(--mp-line)] rounded-sm p-5 mb-8 flex items-start gap-4"
      data-testid="order-guide"
    >
      <Info className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: "var(--mp-text-strong)" }} />
      <div>
        <p
          className="text-sm font-semibold mb-1"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
        >
          How to Order
        </p>
        <ol
          className="text-xs space-y-1 list-decimal list-inside"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
        >
          <li>Select a colour family below to browse shades</li>
          <li>Choose your fabric quality (600, 060, 006, etc.)</li>
          <li>Click on shade numbers to add to cart (1 bundle per click)</li>
          <li>Review your order summary with calculations</li>
          <li>Place order directly or send via WhatsApp/Instagram</li>
        </ol>
        <p className="text-[11px] mt-2 font-medium" style={{ color: "var(--mp-copper)" }}>
          Small Panna = 20m/bundle | Big Panna &amp; Crepe = 24m/bundle | GST 5% extra | Transport
          Rs. 100/parcel (local lorry) or Rs. 150/parcel (bus &amp; travels)
        </p>
      </div>
    </div>
  );
}

function FamilyCard({ family, onSelect }) {
  const last = family.colours[family.colours.length - 1];
  return (
    <button
      onClick={() => onSelect(family)}
      className="product-card border border-[var(--mp-line)] rounded-sm p-5 text-left bg-white"
      data-testid={`family-card-${family.id}`}
    >
      <div className="flex gap-0.5 mb-3 rounded-sm overflow-hidden h-6">
        {family.colours.slice(0, 8).map((num, i) => (
          <div
            key={num}
            className="flex-1"
            style={{ backgroundColor: generateShadeHex(family.hex, i, 8) }}
          />
        ))}
      </div>
      <h3
        className="text-sm font-medium leading-tight"
        style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
      >
        {family.name}
      </h3>
      <p
        className="text-[11px] mt-1"
        style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
      >
        {family.colours.length} shades &middot; #{family.colours[0]}-{last}
      </p>
    </button>
  );
}

export default function FamilyGrid({ families, onSelect, currentQualityName }) {
  return (
    <div>
      <OrderGuide />

      {currentQualityName && (
        <div
          className="mb-6 flex items-center gap-3 px-4 py-3 rounded-sm border border-[var(--mp-copper)]/40 bg-[var(--mp-surface-2)]"
          data-testid="quality-prefill-banner"
        >
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-sm"
            style={{ backgroundColor: "var(--mp-copper)", color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
          >
            Filtered
          </span>
          <span className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
            Showing shades in <strong style={{ color: "var(--mp-text)" }}>{currentQualityName}</strong>. Change quality on the swatches page after picking a family.
          </span>
        </div>
      )}

      <div className="mb-6">
        <h2
          className="text-2xl md:text-3xl font-medium mb-2"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
        >
          Select a Colour Family
        </h2>
        <p
          className="text-sm"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
        >
          {families.length} colour families with 336+ numbered shades
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {families.map((family) => (
          <FamilyCard key={family.id} family={family} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}
